import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs, FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import { ShoppingBag, Minus, Plus, ChevronLeft } from 'lucide-react';
import { useCart } from '../Context/CartContext';
import { getProductById } from '../services/productService';
import { useNotification } from "../components/NotificationContext";
const ProductDetailPage = () => {
  const { id } = useParams();
  const showNotification = useNotification();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const { addToCart } = useCart();
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchProduct = async () => {
      try {
        const productData = await getProductById(id);

        if (productData) {
          setProduct(productData);
        } else {
          navigate(-1); // ürün bulunamazsa ürünler sayfasına yönlendir
        }
      } catch (error) {
        console.error('Ürün verisi alınamadı:', error);
        navigate(-1); // hata olursa da yönlendirme
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id, navigate]);

  const handleAddToCart = () => {
    if (product) {
      // Eğer ürün renk veya beden seçimi istiyorsa ama seçim yapılmadıysa
      if ((product.show_colors && !selectedColor) || (product.show_sizes && !selectedSize)) {
        showNotification('Lütfen renk ve/veya beden seçimi yapınız!', 'error');
        localStorage.setItem('notificationSeverity', 'error');
        return; // Sepete ekleme işlemi yapılmasın
      }
  
      const cartProduct = {
        id: product.id,
        name: product.name,
        price: product.price,
        photo_url: product.photo_url,
        show_sizes: product.show_sizes,
        show_colors: product.show_colors,
        color: selectedColor,
        size: selectedSize
      };
      
      addToCart(cartProduct, quantity);
  
      showNotification('Ürün sepete eklendi!', 'success');
      localStorage.setItem('notificationSeverity', 'success');
    }
  };
  

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const increaseQuantity = () => {
    setQuantity(quantity + 1);
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-secondary-light flex items-center justify-center">
        <div className="text-2xl text-gray-dark">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-secondary-light py-12"
    >
      <div className="container-custom">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-dark hover:text-primary mb-6 transition-colors"
        >
          <ChevronLeft size={20} />
          <span>Geri Dön</span>
        </button>

        <div className="bg-white rounded-lg shadow-medium overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6">
            <div>
              <Swiper
                modules={[Navigation, Thumbs]}
                navigation
                thumbs={{ swiper: thumbsSwiper }}
                className="rounded-lg overflow-hidden mb-3"
              >
               {(product.images || [product.image]).map((image, index) => (
  <SwiperSlide key={index}>
    <div className="aspect-square">
      <img 
        src={`${process.env.REACT_APP_BASE_URL}${typeof image === 'string' ? image : image}`} 
        alt={`${product.name} - görsel ${index + 1}`}
        className="w-full h-full object-contain"
      />
    </div>
  </SwiperSlide>
))}
              </Swiper>

              {product.images && product.images.length > 1 && (
                <Swiper
                  modules={[FreeMode, Navigation, Thumbs]}
                  onSwiper={setThumbsSwiper}
                  spaceBetween={10}
                  slidesPerView={4}
                  freeMode={true}
                  watchSlidesProgress={true}
                  className="thumbs-swiper"
                >
{product.images.map((image, index) => (
  <SwiperSlide key={index}>
    <div className="cursor-pointer aspect-square rounded-md overflow-hidden">
      <img 
        src={`${process.env.REACT_APP_BASE_URL}${image}`} 
        alt={`${product.name} - thumbnail ${index + 1}`}
        className="w-full h-full object-contain"
      />
    </div>
  </SwiperSlide>
))}
                </Swiper>
              )}
            </div>

            <div className="flex flex-col">
              <h1 className="text-3xl font-semibold text-gray-dark mb-2">{product.name}</h1>

              <div className="text-2xl text-primary font-semibold mb-4">
              {Number(product.price).toFixed(2)} ₺
              </div>

              <p className="text-gray mb-6">{product.description}</p>

              {(product.show_colors || product.show_sizes) && (
  <div className="flex gap-4 mb-6">
{/* Renk seçimi */}
{product.show_colors && (
  <div className="flex flex-col">
    <label className="mb-2 font-medium">Renk Seçin:</label>
    <select 
      className="border rounded-md p-2" 
      onChange={(e) => {
        const selectedColor = product.colors.find(color => color.id === parseInt(e.target.value));
        setSelectedColor(selectedColor.color_name);
      }}
      defaultValue="" // Başlangıçta "Renk Seçin" görünmesini sağlamak
    >
      <option value="" disabled>Renk Seçin</option> {/* Varsayılan "Renk Seçin" yazısı */}
      {product.colors.map((color) => (
        <option 
          key={color.id} 
          value={color.id} 
          disabled={!color.in_stock}
        >
          {color.color_name} {color.in_stock ? '' : '(Stokta yok)'}
        </option>
      ))}
    </select>
  </div>
)}

{/* Beden seçimi */}
{product.show_sizes && (
  <div className="flex flex-col">
    <label className="mb-2 font-medium">Beden Seçin:</label>
    <select 
      className="border rounded-md p-2" 
      onChange={(e) => {
        const selectedSize = product.sizes.find(size => size.id === parseInt(e.target.value));
        setSelectedSize(selectedSize.size_name);
      }}
      defaultValue="" // Başlangıçta "Beden Seçin" görünmesini sağlamak
    >
      <option value="" disabled>Beden Seçin</option> {/* Varsayılan "Beden Seçin" yazısı */}
      {product.sizes.map((size) => (
        <option 
          key={size.id} 
          value={size.id} 
          disabled={!size.in_stock}
        >
          {size.size_name} {size.in_stock ? '' : '(Stokta yok)'}
        </option>
      ))}
    </select>
  </div>
)}

  </div>
)}


              <div className="mb-6">
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${product.in_stock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {product.in_stock ? 'Stokta var' : 'Tükendi'}
                </span>
              </div>

              {product.in_stock && (
                <div className="flex items-center mb-6">
                  <span className="mr-4">Adet:</span>
                  <div className="flex items-center border border-gray-200 rounded-md">
                    <button 
                      onClick={decreaseQuantity}
                      className="px-3 py-2 text-gray-dark hover:text-primary transition-colors"
                      aria-label="Decrease quantity"
                    >
                      <Minus size={18} />
                    </button>
                    <span className="px-4 py-2 border-x border-gray-200">{quantity}</span>
                    <button 
                      onClick={increaseQuantity}
                      className="px-3 py-2 text-gray-dark hover:text-primary transition-colors"
                      aria-label="Increase quantity"
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-4 mt-auto">
                {product.in_stock ? (
                  <button 
                    onClick={handleAddToCart}
                    className="flex-grow flex items-center justify-center gap-2 btn-primary"
                  >
                    <ShoppingBag size={20} />
                    Sepete Ekle
                  </button>
                ) : (
                  <button 
                    className="flex-grow flex items-center justify-center gap-2 bg-gray-200 text-gray cursor-not-allowed px-6 py-2 rounded-md"
                    disabled
                  >
                    <ShoppingBag size={20} />
                    Stokta Yok
                  </button>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductDetailPage;